import React, { useCallback, useEffect, useState } from 'react';

import '../DiscountModal/DiscountModal.scss';
import Modal from '@wetransfer/transfer/component/Modal/Modal';
import PAYLOAD_PROPS from '@wetransfer/transfer/constant/experiments/discount-modal';
import { AppConfig } from '@wetransfer/transfer/core-bundle/config/app-config';
import Helper from '@wetransfer/transfer/helper';
import SplashpageHelper from '@wetransfer/transfer/helper/splashpage';
import Analytics from '@wetransfer/transfer/modules/analytics';
import { trackAmplitudeEvent } from '@wetransfer/transfer/modules/analytics/vendors/amplitude';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';
import I18n from '@wetransfer/transfer/modules/i18n';
import { Button, Icon } from '@wetransfer/transfer/oneui/react';
import BlackFridayImage from '@wetransfer/transfer/public/assets/images/discountModal/modalBlackFriday.png';
import CyberMondayImage from '@wetransfer/transfer/public/assets/images/discountModal/modalCyberMonday.png';
import LastChanceImage from '@wetransfer/transfer/public/assets/images/discountModal/modalLastChance.png';

const listItems = [
  I18n.t('discount.modal.argument.size_limit'),
  I18n.t('discount.modal.argument.storage_size'),
  I18n.t('discount.modal.argument.unlimited_review'),
];

interface Props {
  payload: Array<PAYLOAD_PROPS>;
  onDismissModal(): void;
}

export default function DiscountModal({
  payload,
  onDismissModal,
}: Props): JSX.Element {
  const [showModal, setShowModal] = useState(
    SplashpageHelper.shouldDisplaySplashpage()
  );
  const discountData = Helper.getCurrentExperimentData(payload);

  const discountCode = discountData?.couponCode;
  const discountRate = discountData?.percentageDiscount;
  const payloadImage: string = discountData?.campaignImage;
  const discountTitle = discountData?.campaignTitle;

  const imageURL = {
    modalBlackFriday: BlackFridayImage,
    modalCyberMonday: CyberMondayImage,
    modalLastChance: LastChanceImage,
  }[payloadImage || 'modalLastChance'];

  const mainCtaUrl = `/sign-up?tier=pro_2022&billing=yearly&coupon=${discountCode}`;

  const titleContent = I18n.t('discount.modal.title', {
    discount_amount: discountRate,
  });

  const displayModal = showModal && discountData !== undefined;

  const discountCodeContent = I18n.t('discount.modal.subtitle', {
    coupon_code_name: discountCode,
  });

  const textContent = I18n.t('discount.modal.text_content', {
    discount_amount: discountRate,
  });

  const amplitudeTracking = useCallback(
    (action: string) => {
      trackAmplitudeEvent({
        action: 'Upsell',
        additionalProperties: {
          Type: 'Modal',
          Context: `Discount Campaign: ${discountTitle}`,
          Action: action,
        },
      });
    },
    [discountTitle]
  );

  const snowplowTracking = useCallback((action: string) => {
    Analytics.trackSnowplowEvent({
      category: 'upsell',
      action: action,
      property: 'black_friday',
    });
  }, []);

  const dismissModal = useCallback(() => {
    setShowModal(false);
    onDismissModal();
    snowplowTracking('click_dismiss_modal');
    amplitudeTracking('Dismiss');
  }, [amplitudeTracking, onDismissModal, snowplowTracking]);

  const redeemDiscount = useCallback(() => {
    setShowModal(false);
    onDismissModal();
    Cookie.set(AppConfig.cookies.discountModalSeen.name, true);
    snowplowTracking('click_upgrade_modal');
    amplitudeTracking('Upgrade');
  }, [amplitudeTracking, onDismissModal, snowplowTracking]);

  useEffect(() => {
    if (showModal) {
      snowplowTracking('show_modal');
      amplitudeTracking('Shown');
    }
  }, [amplitudeTracking, snowplowTracking, showModal]);

  return (
    <Modal showModal={displayModal} contentHeight="100vh">
      <section className="DiscountModal" data-testid="DiscountModal">
        <div className="DiscountModal__image">
          <img src={imageURL} alt="Illustration discount deal" />
        </div>
        <div className="DiscountModal__content">
          <h2 className="DiscountModal__content--title">{titleContent}</h2>
          <p className="DiscountModal__content--discountCode">
            {discountCodeContent}
          </p>
          <p className="DiscountModal__content--info">{textContent}</p>
          <ul className="DiscountModal__content--checkItems">
            {listItems.map((item, index) => {
              return (
                <li className="DiscountModal__content--item" key={index}>
                  <Icon
                    className="item-checkmark"
                    name="checkmark--blue"
                    size={12}
                  />
                  <span>{item}</span>
                </li>
              );
            })}
          </ul>
          <div className="DiscountModal__actionButtons">
            <Button
              as="a"
              className="DiscountModal__actionButtons--primary"
              variant="primary"
              label={I18n.t('discount.modal.cta_label')}
              square={true}
              small={true}
              onClick={redeemDiscount}
              href={mainCtaUrl}
            />
            <Button
              variant="secondary"
              label={I18n.t('discount.modal.dismiss_label')}
              square={true}
              small={true}
              onClick={dismissModal}
            />
          </div>
        </div>
      </section>
    </Modal>
  );
}
